import { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { Checkbox, FormControl, FormControlLabel, Grid } from '@mui/material';
import type { ProductWithCurrencies } from '@shared/api';
import { Input } from '@shared/widgets';

interface Props {
  products: ProductWithCurrencies[];
}

export const StepCurrency = ({ products }: Props) => {
  const { control, watch, setValue } = useFormContext();
  const productName = watch('productName');
  const eurCurrency = watch('eurCurrency');
  const chfCurrency = watch('chfCurrency');
  const usdCurrency = watch('usdCurrency');

  const product = useMemo(() => products.find((product) => product.name === productName), [productName, products]);

  const showEur = !product || !!product.currencies['EUR'];
  const showChf = !product || !!product.currencies['CHF'];
  const showUsd = !product || !!product.currencies['USD'];

  let selectedCount = 0;
  if (eurCurrency) selectedCount++;
  if (chfCurrency) selectedCount++;
  if (usdCurrency) selectedCount++;

  let hasDisabled = false;
  if (product?.maxCurrencies) hasDisabled = selectedCount >= product.maxCurrencies;

  const disabledEur = hasDisabled && !eurCurrency;
  const disabledChf = hasDisabled && !chfCurrency;
  const disabledUsd = hasDisabled && !usdCurrency;

  const resetBankAccountNumber = () => {
    setValue('bankAccountNumber', '');
    setValue('bankAccountNumber2', '');
  };

  return (
    <Grid container style={{ alignItems: 'center', justifyContent: 'center' }}>
      {showEur && (
        <Grid sm={12} md={4} item>
          <FormControl>
            <FormControlLabel
              label='EUR'
              labelPlacement='end'
              control={
                <Controller
                  name='eurCurrency'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      color='primary'
                      value={field.value}
                      checked={field.value}
                      disabled={disabledEur}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('eurCreditLimit', '');
                        resetBankAccountNumber();
                      }}
                    />
                  )}
                />
              }
            />
          </FormControl>
          {eurCurrency && (
            <Controller
              name='eurCreditLimit'
              control={control}
              rules={{ required: 'EUR credit limit is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input {...field} label='Specify EUR credit limit' required error={!!error} helperText={error?.message} />
              )}
            />
          )}
        </Grid>
      )}
      {showChf && (
        <Grid sm={12} md={4} item>
          <FormControl>
            <FormControlLabel
              label='CHF'
              labelPlacement='end'
              control={
                <Controller
                  name='chfCurrency'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      color='primary'
                      {...field}
                      checked={field.value}
                      disabled={disabledChf}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('chfCreditLimit', '');
                        resetBankAccountNumber();
                      }}
                    />
                  )}
                />
              }
            />
          </FormControl>
          {chfCurrency && (
            <Controller
              name='chfCreditLimit'
              control={control}
              rules={{ required: 'CHF credit limit is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input {...field} label='Specify CHF credit limit' required error={!!error} helperText={error?.message} />
              )}
            />
          )}
        </Grid>
      )}
      {showUsd && (
        <Grid sm={12} md={4} item>
          <FormControl>
            <FormControlLabel
              label='USD'
              labelPlacement='end'
              control={
                <Controller
                  name='usdCurrency'
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      color='primary'
                      {...field}
                      checked={field.value}
                      disabled={disabledUsd}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue('usdCreditLimit', '');
                        resetBankAccountNumber();
                      }}
                    />
                  )}
                />
              }
            />
          </FormControl>
          {usdCurrency && (
            <Controller
              name='usdCreditLimit'
              control={control}
              rules={{ required: 'USD credit limit is required' }}
              render={({ field, fieldState: { error } }) => (
                <Input {...field} label='Specify USD credit limit' required error={!!error} helperText={error?.message} />
              )}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};
